/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Text, View } from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import { styles } from '../ReportsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { formatMoney, formatMoneyCompact } from '../../../shared/format/public-api';
import { ticketColors } from '../../../shared/theme/ticketColors';

export type PaymentMixItem = { methodId: string; total: number };

type Props = {
  items: PaymentMixItem[];
  methodNames?: Record<string, string>;
  total: number;
};

const DONUT_COLORS = [
  ticketColors.gold,
  ticketColors.ink2,
  ticketColors.warn,
  '#8da9a3',
  ticketColors.ok,
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  ticketColors.bad,
  '#64748b',
];

export function ReportsPaymentMixDonut({ items, methodNames, total }: Props) {
  const sorted = [...items].sort((a, b) => b.total - a.total);
  const radius = 57;
  const strokeWidth = 30;
  const circumference = 2 * Math.PI * radius;
  const center = radius + strokeWidth / 2;
  const size = center * 2;

  let offset = 0;
  const segments = sorted.map((item, idx) => {
    const pct = total > 0 ? item.total / total : 0;
    const segmentLength = pct * circumference;
    const segment = {
      item,
      pct,
      color: DONUT_COLORS[idx % DONUT_COLORS.length],
      segmentLength,
      offset,
    };
    offset += segmentLength;
    return segment;
  });

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.md, flexWrap: 'wrap' }}>
      <View style={{ width: size, height: size, position: 'relative' }}>
        <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke={ticketColors.rule}
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          {segments.map((s, i) => (
            <Circle
              key={`${s.item.methodId}-${i}`}
              cx={center}
              cy={center}
              r={radius}
              stroke={s.color}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={`${s.segmentLength}, ${circumference - s.segmentLength}`}
              strokeDashoffset={-s.offset}
              strokeLinecap="butt"
            />
          ))}
        </Svg>
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            justifyContent: 'center',
            alignItems: 'center',
          }}
          pointerEvents="none"
        >
          <Text
            style={[styles.reportsDenseListStatItemValue, { fontSize: 16 }]}
            numberOfLines={1}
            accessibilityLabel={`Total ${formatMoney(total)}`}
          >
            {formatMoneyCompact(total)}
          </Text>
          <Text style={styles.caption}>Total</Text>
        </View>
      </View>

      <View style={{ flex: 1, minWidth: 160, gap: spacing.xs }}>
        {segments.map((s, i) => {
          const label = methodNames?.[s.item.methodId] ?? s.item.methodId;
          return (
            <View key={`${s.item.methodId}-${i}`} style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
              <View
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: s.color,
                }}
              />
              <Text style={[styles.rowLabel, { flex: 1 }]} numberOfLines={1}>
                {label}
              </Text>
              <Text style={styles.rowValue}>
                {formatMoney(s.item.total)} · {(s.pct * 100).toFixed(0)}%
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}
